import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  FAQContent,
  FAQHeader,
  FAQBack,
  FAQTitle,
  FAQSortDiv,
  FAQSort,
  FAQBoard as FAQBoardContainer,
  FAQInquiry,
} from './FAQ.styles';
import FAQBoard from 'components/FAQBoard/FAQBoard';
import { faqService, FAQItem, FAQType } from 'services/faqService';

const FAQ: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [faqTypes, setFaqTypes] = useState<FAQType[]>([]);
  const [selectedSort, setSelectedSort] = useState<string>('');
  const [faqData, setFaqData] = useState<FAQItem[]>([]);
  const [expandedState, setExpandedState] = useState<{ [key: number]: boolean }>({});
  const faqRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // FAQ 타입 목록 가져오기
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const types = await faqService.getFAQTypes();
        setFaqTypes(types);
        if (types.length > 0) {
          setSelectedSort(types[0].name);
        }
      } catch (error) {
        console.error('FAQ 타입을 가져오는 중 오류 발생:', error);
      }
    };

    fetchTypes();
  }, []);

  // FAQ 데이터 가져오기
  useEffect(() => {
    if (!selectedSort) return;

    const fetchData = async () => {
      try {
        const data = await faqService.getFAQs(selectedSort);
        setFaqData(data);
      } catch (error) {
        console.error('FAQ 데이터를 가져오는 중 오류 발생:', error);
      }
    };

    fetchData();
    setExpandedState({});
  }, [selectedSort]);

  // URL 쿼리 파라미터에서 id를 읽어서 해당 FAQ의 타입 자동 선택
  useEffect(() => {
    const faqId = searchParams.get('id');
    if (!faqId || faqTypes.length === 0) return;

    const targetId = Number(faqId);

    // 모든 타입의 FAQ를 검색하여 해당 FAQ가 속한 타입 찾기
    const findFAQType = async () => {
      try {
        for (const type of faqTypes) {
          const data = await faqService.getFAQs(type.name);
          const foundFAQ = data.find(faq => faq.id === targetId);
          if (foundFAQ) {
            setSelectedSort(type.name);
            break;
          }
        }
      } catch (error) {
        console.error('FAQ 타입 찾기 실패:', error);
      }
    };

    findFAQType();
  }, [searchParams, faqTypes]);

  // URL 쿼리 파라미터에서 id를 읽어서 해당 FAQ 펼치기 및 스크롤
  useEffect(() => {
    const faqId = searchParams.get('id');
    if (!faqId || faqData.length === 0) return;

    const targetId = Number(faqId);
    const targetIndex = faqData.findIndex(faq => faq.id === targetId);

    if (targetIndex !== -1) {
      // 해당 FAQ 펼치기
      setExpandedState(prev => ({
        ...prev,
        [targetIndex]: true
      }));

      // 약간의 지연 후 스크롤 (DOM 렌더링 대기)
      setTimeout(() => {
        const targetElement = faqRefs.current[targetIndex];
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, [searchParams, faqData]);

  const handleToggle = (index: number) => {
    setExpandedState((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const handleSortClick = (sort: string) => {
    setSelectedSort(sort);
  };

  const handleBackClick = () => {
    window.history.back();
  };

  return (
    <FAQContent>
      <FAQHeader>
        <FAQBack onClick={handleBackClick}>
          <FontAwesomeIcon icon="chevron-left" style={{ fontSize: '1.5em' }} />
        </FAQBack>
        <FAQTitle>FAQ</FAQTitle>
      </FAQHeader>
      <FAQSortDiv>
        {faqTypes.map((type) => (
          <FAQSort
            key={type.name}
            onClick={() => handleSortClick(type.name)}
            className={selectedSort === type.name ? 'active' : ''}
          >
            {type.displayName}
          </FAQSort>
        ))}
      </FAQSortDiv>
      <FAQBoardContainer>
        {faqData.map((faq, index) => (
          <div key={faq.id} ref={el => { faqRefs.current[index] = el; }}>
            <FAQBoard
              title={faq.question}
              initialContent={faq.answer}
              expanded={expandedState[index] || false}
              onToggle={() => handleToggle(index)}
            />
          </div>
        ))}
      </FAQBoardContainer>
      <FAQInquiry to="/inquiry">문의하기</FAQInquiry>
    </FAQContent>
  );
};

export default FAQ;
